import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import type {
  CaseSubmission,
  DailyReport,
  Mistake,
  PlanRevision,
  PracticeAttempt,
  ReviewEvent,
  SessionStatus,
  StudyDay,
} from "../domain/models";
import {
  caseSubmissionSchema,
  dailyReportSchema,
  mistakeSchema,
  planRevisionSchema,
  practiceAttemptSchema,
  reviewEventSchema,
  studyDaySchema,
} from "../domain/schemas";
import { assertSessionTransition } from "../domain/state-machines";
import { prepareImageAttachment } from "../data/attachments";
import {
  createJsonBackupBlob,
  downloadJsonBackup,
  restoreJsonBackup,
} from "../data/backup";
import { bootstrapStudyData } from "../data/bootstrap";
import { IndexedDbStudyRepository } from "../data/repository";
import { createStudyRecord } from "../data/schema";
import { runSync } from "../data/sync";
import type { AttachmentMetadata, RestoreMode, SyncRunResult } from "../data/types";
import type { RecordSource } from "../data/types";

export interface SessionProgress {
  sessionId: string;
  status: SessionStatus;
  elapsedSeconds: number;
  completedStepIds: string[];
  masteryByNode: Record<string, "stable" | "uncertain" | "unknown">;
  note: string;
  updatedAt: string;
}

export interface AppNotice {
  tone: "success" | "warning" | "error" | "info";
  message: string;
}

interface StudyState {
  ready: boolean;
  storageError?: string;
  sessionProgress: Record<string, SessionProgress>;
  practiceAttempts: PracticeAttempt[];
  mistakes: Mistake[];
  reports: DailyReport[];
  caseSubmissions: CaseSubmission[];
  reviewEvents: ReviewEvent[];
  planRevisions: PlanRevision[];
  planDays: StudyDay[];
  planHistory: StudyDay[];
  attachmentMetadata: AttachmentMetadata[];
  attachmentCount: number;
  pendingSyncCount: number;
  snapshotCount: number;
  notice?: AppNotice;
  syncResult?: SyncRunResult;
}

interface StudyContextValue extends StudyState {
  updateSession: (sessionId: string, patch: Partial<SessionProgress>) => Promise<void>;
  savePracticeAttempt: (attempt: PracticeAttempt) => Promise<void>;
  savePracticeAttempts: (attempts: PracticeAttempt[]) => Promise<void>;
  saveReport: (report: DailyReport) => Promise<void>;
  saveMistake: (mistake: Mistake) => Promise<void>;
  saveCaseSubmission: (submission: CaseSubmission) => Promise<void>;
  saveReviewEvents: (events: ReviewEvent[]) => Promise<void>;
  savePlanRevision: (revision: PlanRevision) => Promise<void>;
  savePlanDay: (day: StudyDay) => Promise<void>;
  addImages: (ownerType: "daily-report" | "mistake" | "case-submission" | "study-session", ownerId: string, files: File[]) => Promise<string[]>;
  exportBackup: () => Promise<void>;
  restoreBackup: (file: File, mode: RestoreMode) => Promise<void>;
  syncNow: (options?: { silent?: boolean }) => Promise<SyncRunResult>;
  clearNotice: () => void;
  reload: () => Promise<void>;
}

const repository = new IndexedDbStudyRepository();

const StudyContext = createContext<StudyContextValue | null>(null);

const initialState: StudyState = {
  ready: false,
  sessionProgress: {},
  practiceAttempts: [],
  mistakes: [],
  reports: [],
  caseSubmissions: [],
  reviewEvents: [],
  planRevisions: [],
  planDays: [],
  planHistory: [],
  attachmentMetadata: [],
  attachmentCount: 0,
  pendingSyncCount: 0,
  snapshotCount: 0,
};

function selectLatestPlanDays(history: StudyDay[]): StudyDay[] {
  const latest = new Map<string, StudyDay>();
  for (const day of history) {
    const previous = latest.get(day.date);
    if (!previous || day.planVersion > previous.planVersion) latest.set(day.date, day);
  }
  return [...latest.values()].sort((left, right) => left.date.localeCompare(right.date));
}

function asSessionProgress(value: unknown): SessionProgress | undefined {
  if (!value || typeof value !== "object") return undefined;
  const payload = value as Partial<SessionProgress>;
  if (!payload.sessionId || !payload.status) return undefined;
  return {
    sessionId: payload.sessionId,
    status: payload.status,
    elapsedSeconds: Number(payload.elapsedSeconds) || 0,
    completedStepIds: Array.isArray(payload.completedStepIds) ? payload.completedStepIds : [],
    masteryByNode: payload.masteryByNode ?? {},
    note: typeof payload.note === "string" ? payload.note : "",
    updatedAt: typeof payload.updatedAt === "string" ? payload.updatedAt : new Date().toISOString(),
  };
}

export function StudyProvider({ children }: PropsWithChildren) {
  const [state, setState] = useState<StudyState>(initialState);
  const activeSyncRef = useRef<Promise<SyncRunResult> | null>(null);

  const reload = useCallback(async () => {
    try {
      await bootstrapStudyData(repository);
      const [
        progressRecords,
        practiceRecords,
        mistakeRecords,
        reportRecords,
        caseRecords,
        reviewRecords,
        revisionRecords,
        planRecords,
        attachments,
        outbox,
        snapshots,
      ] = await Promise.all([
        repository.listRecords({ type: "task-completion" }),
        repository.listRecords({ type: "practice-attempt" }),
        repository.listRecords({ type: "mistake" }),
        repository.listRecords({ type: "daily-report" }),
        repository.listRecords({ type: "case-submission" }),
        repository.listRecords({ type: "review-event" }),
        repository.listRecords({ type: "plan-revision" }),
        repository.listRecords({ type: "plan-day" }),
        repository.listAttachments(),
        repository.listOutbox(),
        repository.listSnapshots(),
      ]);
      const sessionProgress = Object.fromEntries(
        progressRecords
          .map((record) => asSessionProgress(record.payload))
          .filter((item): item is SessionProgress => Boolean(item))
          .map((item) => [item.sessionId, item]),
      );
      const planHistory = planRecords
        .map((record) => record.payload as unknown as StudyDay)
        .sort((left, right) => left.date.localeCompare(right.date) || left.planVersion - right.planVersion);
      setState((current) => ({
        ...current,
        ready: true,
        storageError: undefined,
        sessionProgress,
        practiceAttempts: practiceRecords.map((record) => record.payload as unknown as PracticeAttempt),
        mistakes: mistakeRecords.map((record) => record.payload as unknown as Mistake),
        reports: reportRecords.map((record) => record.payload as unknown as DailyReport),
        caseSubmissions: caseRecords.map((record) => record.payload as unknown as CaseSubmission),
        reviewEvents: reviewRecords.map((record) => record.payload as unknown as ReviewEvent),
        planRevisions: revisionRecords.map((record) => record.payload as unknown as PlanRevision),
        planDays: selectLatestPlanDays(planHistory),
        planHistory,
        attachmentMetadata: attachments.map(({ blob: _blob, ...metadata }) => metadata),
        attachmentCount: attachments.length,
        pendingSyncCount: outbox.length,
        snapshotCount: snapshots.length,
      }));
    } catch (error) {
      setState((current) => ({
        ...current,
        ready: true,
        storageError: error instanceof Error ? error.message : String(error),
      }));
    }
  }, []);

  useEffect(() => {
    void reload();
  }, [reload]);

  const updateSession = useCallback(async (sessionId: string, patch: Partial<SessionProgress>) => {
    const previous = state.sessionProgress[sessionId] ?? {
      sessionId,
      status: "not_started" as const,
      elapsedSeconds: 0,
      completedStepIds: [],
      masteryByNode: {},
      note: "",
      updatedAt: new Date().toISOString(),
    };
    const next: SessionProgress = {
      ...previous,
      ...patch,
      sessionId,
      updatedAt: new Date().toISOString(),
    };
    if (next.status !== previous.status) assertSessionTransition(previous.status, next.status);
    const deviceId = await repository.getDeviceId();
    const existing = await repository.getRecord("task-completion", sessionId);
    const record = createStudyRecord({
      id: sessionId,
      type: "task-completion",
      deviceId,
      studyDate: sessionId.slice(0, 10),
      payload: next as unknown as Record<string, unknown>,
    });
    await repository.saveRecord({
      ...record,
      createdAt: existing?.createdAt ?? record.createdAt,
      revision: existing?.revision ?? record.revision,
    });
    setState((current) => ({
      ...current,
      sessionProgress: { ...current.sessionProgress, [sessionId]: next },
      pendingSyncCount: current.pendingSyncCount + 1,
      notice: { tone: "success", message: "学习进度已保存到本机" },
    }));
  }, [state.sessionProgress]);

  const saveTypedRecord = useCallback(async <T extends Record<string, unknown>>(
    type: "practice-attempt" | "daily-report" | "mistake" | "case-submission" | "review-event" | "plan-revision" | "plan-day",
    id: string,
    studyDate: string,
    payload: T,
    source: RecordSource = "app",
  ) => {
    const deviceId = await repository.getDeviceId();
    const existing = await repository.getRecord(type, id);
    const record = createStudyRecord({ id, type, studyDate, payload, deviceId, source });
    await repository.saveRecord({
      ...record,
      createdAt: existing?.createdAt ?? record.createdAt,
      revision: existing?.revision ?? record.revision,
    });
  }, []);

  const savePracticeAttempts = useCallback(async (attempts: PracticeAttempt[]) => {
    const parsed = practiceAttemptSchema.array().min(1).parse(attempts);
    const deviceId = await repository.getDeviceId();
    const records = parsed.map((attempt) => createStudyRecord({
      id: attempt.id,
      type: "practice-attempt" as const,
      studyDate: attempt.startedAt?.slice(0, 10) ?? attempt.dayId.slice(-10),
      payload: attempt as unknown as Record<string, unknown>,
      deviceId,
      source: "app" as const,
    }));
    await repository.saveRecords(records);
    const ids = new Set(parsed.map((attempt) => attempt.id));
    setState((current) => ({
      ...current,
      practiceAttempts: [...current.practiceAttempts.filter((item) => !ids.has(item.id)), ...parsed],
      pendingSyncCount: current.pendingSyncCount + parsed.length,
      notice: { tone: "success", message: parsed.length === 1 ? "题库数据已记录" : `已原子保存${parsed.length}条分科题库记录` },
    }));
  }, []);

  const savePracticeAttempt = useCallback(async (attempt: PracticeAttempt) => {
    await savePracticeAttempts([attempt]);
  }, [savePracticeAttempts]);

  const saveReport = useCallback(async (report: DailyReport) => {
    const parsed = dailyReportSchema.parse(report);
    await saveTypedRecord("daily-report", parsed.id, parsed.date, parsed as unknown as Record<string, unknown>);
    setState((current) => ({
      ...current,
      reports: [...current.reports.filter((item) => item.id !== parsed.id), parsed],
      pendingSyncCount: current.pendingSyncCount + 1,
      notice: {
        tone: "success",
        message: parsed.status === "accepted"
          ? "晚报已通过Codex验收"
          : parsed.status === "revision_required"
            ? "Codex已要求补交或修订"
            : parsed.status === "submitted"
              ? "晚报已提交并锁定版本"
              : "晚报草稿已保存",
      },
    }));
  }, [saveTypedRecord]);

  const saveMistake = useCallback(async (mistake: Mistake) => {
    const parsed = mistakeSchema.parse(mistake);
    await saveTypedRecord("mistake", parsed.id, parsed.createdDate, parsed as unknown as Record<string, unknown>);
    setState((current) => ({
      ...current,
      mistakes: [...current.mistakes.filter((item) => item.id !== parsed.id), parsed],
      pendingSyncCount: current.pendingSyncCount + 1,
      notice: {
        tone: "success",
        message: current.mistakes.some((item) => item.id === parsed.id)
          ? "回炉结果已保存"
          : "典型错题已归档并生成D1/D3/D7",
      },
    }));
  }, [saveTypedRecord]);

  const saveCaseSubmission = useCallback(async (submission: CaseSubmission) => {
    const parsed = caseSubmissionSchema.parse(submission);
    await saveTypedRecord("case-submission", parsed.id, parsed.dayId.slice(-10), parsed as unknown as Record<string, unknown>);
    setState((current) => ({
      ...current,
      caseSubmissions: [...current.caseSubmissions.filter((item) => item.id !== parsed.id), parsed],
      pendingSyncCount: current.pendingSyncCount + 1,
      notice: { tone: "success", message: parsed.status === "draft" ? "案例草稿已保存" : "案例训练已提交" },
    }));
  }, [saveTypedRecord]);

  const saveReviewEvents = useCallback(async (events: ReviewEvent[]) => {
    const parsed = events.map((event) => reviewEventSchema.parse(event));
    await Promise.all(parsed.map((event) => saveTypedRecord(
      "review-event",
      event.id,
      event.createdAt.slice(0, 10),
      event as unknown as Record<string, unknown>,
      "coach",
    )));
    setState((current) => ({
      ...current,
      reviewEvents: [
        ...current.reviewEvents.filter((item) => !parsed.some((event) => event.id === item.id)),
        ...parsed,
      ],
      pendingSyncCount: current.pendingSyncCount + parsed.length,
      notice: { tone: "success", message: `已写入口试与验收记录${parsed.length}条` },
    }));
  }, [saveTypedRecord]);

  const savePlanRevision = useCallback(async (revision: PlanRevision) => {
    const parsed = planRevisionSchema.parse(revision);
    await saveTypedRecord("plan-revision", parsed.id, parsed.planDate, parsed as unknown as Record<string, unknown>, "coach");
    setState((current) => ({
      ...current,
      planRevisions: [...current.planRevisions.filter((item) => item.id !== parsed.id), parsed],
      pendingSyncCount: current.pendingSyncCount + 1,
      notice: { tone: "success", message: `计划修订v${parsed.fromVersion}→v${parsed.toVersion}已归档` },
    }));
  }, [saveTypedRecord]);

  const savePlanDay = useCallback(async (day: StudyDay) => {
    const parsed = studyDaySchema.parse(day);
    await saveTypedRecord("plan-day", `${parsed.id}-v${parsed.planVersion}`, parsed.date, parsed as unknown as Record<string, unknown>, "import");
    setState((current) => ({
      ...current,
      planDays: selectLatestPlanDays([...current.planDays, parsed]),
      planHistory: [...current.planHistory.filter((item) => !(item.date === parsed.date && item.planVersion === parsed.planVersion)), parsed]
        .sort((left, right) => left.date.localeCompare(right.date) || left.planVersion - right.planVersion),
      pendingSyncCount: current.pendingSyncCount + 1,
      notice: { tone: "success", message: `${parsed.date}任务版本v${parsed.planVersion}已导入` },
    }));
  }, [saveTypedRecord]);

  const addImages = useCallback(async (
    ownerType: "daily-report" | "mistake" | "case-submission" | "study-session",
    ownerId: string,
    files: File[],
  ) => {
    const deviceId = await repository.getDeviceId();
    const saved: string[] = [];
    const savedMetadata: AttachmentMetadata[] = [];
    for (const file of files.slice(0, 20)) {
      const attachment = await prepareImageAttachment(file, {
        ownerType,
        ownerId,
        deviceId,
        fileName: file.name,
      }, { maxBytes: 10 * 1024 * 1024, maxLongEdge: 2200, quality: 0.88 });
      await repository.saveAttachment(attachment);
      saved.push(attachment.id);
      const { blob: _blob, ...metadata } = attachment;
      savedMetadata.push(metadata);
    }
    setState((current) => ({
      ...current,
      attachmentMetadata: [
        ...current.attachmentMetadata.filter((item) => !savedMetadata.some((savedItem) => savedItem.id === item.id)),
        ...savedMetadata,
      ],
      attachmentCount: current.attachmentCount + saved.length,
      pendingSyncCount: current.pendingSyncCount + saved.length,
      notice: { tone: "success", message: `已保存${saved.length}张图片并生成适配预览` },
    }));
    return saved;
  }, []);

  const exportBackup = useCallback(async () => {
    const blob = await createJsonBackupBlob(repository, {
      includeAttachments: true,
      includeOutbox: true,
    });
    downloadJsonBackup(blob);
    setState((current) => ({ ...current, notice: { tone: "success", message: "完整备份已下载" } }));
  }, []);

  const restoreBackup = useCallback(async (file: File, mode: RestoreMode) => {
    const result = await restoreJsonBackup(repository, file, { mode, createSafetySnapshot: true });
    await reload();
    setState((current) => ({
      ...current,
      notice: { tone: "success", message: `恢复完成：${result.records}条记录、${result.attachments}个附件` },
    }));
  }, [reload]);

  const syncNow = useCallback(async (options: { silent?: boolean } = {}) => {
    const task = activeSyncRef.current ?? (async () => {
      const { createSyncAdapterFromEnvironment } = await import("../data/supabase");
      const result = await runSync(repository, createSyncAdapterFromEnvironment());
      await reload();
      setState((current) => ({ ...current, syncResult: result }));
      return result;
    })();
    activeSyncRef.current = task;
    try {
      const result = await task;
      if (!options.silent || result.mode !== "synced") {
        const notice: AppNotice = {
          tone: result.mode === "synced" ? "success" : result.mode === "partial" ? "warning" : "info",
          message: result.mode === "offline"
            ? "云端尚未配置，数据继续安全保存在本机"
            : `同步完成：上传${result.pushed}、下载${result.pulled}、待处理${result.pending}`,
        };
        setState((current) => ({ ...current, notice }));
      }
      return result;
    } finally {
      if (activeSyncRef.current === task) activeSyncRef.current = null;
    }
  }, [reload]);

  const clearNotice = useCallback(() => {
    setState((current) => ({ ...current, notice: undefined }));
  }, []);

  const value = useMemo<StudyContextValue>(() => ({
    ...state,
    updateSession,
    savePracticeAttempt,
    savePracticeAttempts,
    saveReport,
    saveMistake,
    saveCaseSubmission,
    saveReviewEvents,
    savePlanRevision,
    savePlanDay,
    addImages,
    exportBackup,
    restoreBackup,
    syncNow,
    clearNotice,
    reload,
  }), [
    state,
    updateSession,
    savePracticeAttempt,
    savePracticeAttempts,
    saveReport,
    saveMistake,
    saveCaseSubmission,
    saveReviewEvents,
    savePlanRevision,
    savePlanDay,
    addImages,
    exportBackup,
    restoreBackup,
    syncNow,
    clearNotice,
    reload,
  ]);

  return <StudyContext.Provider value={value}>{children}</StudyContext.Provider>;
}

export function useStudy() {
  const context = useContext(StudyContext);
  if (!context) throw new Error("useStudy 必须在 StudyProvider 内使用");
  return context;
}
